import { supabase } from '../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Shared server-readiness gate.
 * 1. Sends a no-cors wake-up ping (no console CORS errors).
 * 2. Alternates between silent no-cors pings and a quick CORS check.
 *    The no-cors pings keep waking Render; the CORS check confirms readiness.
 * 3. All parallel callers share the same promise.
 * 4. Once confirmed, future calls skip entirely.
 */
let _serverReady = false;
let _readyPromise = null;

async function ensureServerReady() {
  if (_serverReady) return;
  if (_readyPromise) return _readyPromise;

  _readyPromise = (async () => {
    // Fire initial no-cors wake-up ping
    try { await fetch(`${API_URL}/health`, { mode: 'no-cors' }); } catch (_) {}

    for (let i = 0; i < 30; i++) {           // 30 × 3 s = 90 s max
      // Silent no-cors ping each iteration to keep Render alive
      try { await fetch(`${API_URL}/health`, { mode: 'no-cors' }); } catch (_) {}

      // Quick CORS health check with AbortController timeout
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 2000);
        const res = await fetch(`${API_URL}/health`, { signal: ctrl.signal });
        clearTimeout(timer);
        if (res.ok) { _serverReady = true; return; }
      } catch (_) { /* CORS error, abort, or network error — still waking */ }

      await new Promise(r => setTimeout(r, 3000));
    }
    _readyPromise = null;
    throw new Error('Server is not responding. Please try again later.');
  })();

  return _readyPromise;
}

/** Reset the gate so the next call retries from scratch. */
export function resetServerReady() {
  _serverReady = false;
  _readyPromise = null;
}

async function adminFetch(path, options = {}) {
  // Wait for the server to be alive before making CORS-sensitive requests
  await ensureServerReady();

  const token = await getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_URL}/api/admin${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || 'Admin API request failed');
  }

  const json = await response.json();
  return json.data;
}

// Dashboard stats
export async function getAdminStats() {
  return adminFetch('/stats');
}

// Atlas files
export async function getPendingAtlasFiles() {
  return adminFetch('/atlas/pending');
}

export async function getAllAtlasFiles() {
  return adminFetch('/atlas/all');
}

export async function approveAtlasFile(id) {
  return adminFetch(`/atlas/${id}/approve`, { method: 'POST' });
}

export async function rejectAtlasFile(id) {
  return adminFetch(`/atlas/${id}/reject`, { method: 'POST' });
}

export async function unpublishAtlasFile(id) {
  return adminFetch(`/atlas/${id}/unpublish`, { method: 'POST' });
}

// Itineraries
export async function getPendingItineraries() {
  return adminFetch('/itineraries/pending');
}

export async function getAllItineraries() {
  return adminFetch('/itineraries/all');
}

export async function approveItinerary(id) {
  return adminFetch(`/itineraries/${id}/approve`, { method: 'POST' });
}

export async function rejectItinerary(id) {
  return adminFetch(`/itineraries/${id}/reject`, { method: 'POST' });
}

export async function unpublishItinerary(id) {
  return adminFetch(`/itineraries/${id}/unpublish`, { method: 'POST' });
}

// Users
export async function getUsers() {
  return adminFetch('/users');
}

export async function updateUserRole(id, role) {
  return adminFetch(`/users/${id}/role`, {
    method: 'POST',
    body: JSON.stringify({ role }),
  });
}
